import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import type { RegistrationField } from '../services/registrationService'

/** Parse a newline-delimited options string into a trimmed, non-empty list. */
function parseFieldOptions(raw?: string | null): string[] {
  return (raw ?? '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Renders a single admin-configured registration field as the appropriate MUI
 * input (text / textarea / select / radio). Shared by the in-app RegisterPage
 * and the public self-service registration page so the form stays consistent.
 */
export default function RegistrationFieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: RegistrationField
  value: string | undefined
  error?: boolean
  onChange: (value: string) => void
}) {
  const label = (
    <>
      {field.label}
      {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
    </>
  )
  const opts = parseFieldOptions(field.options)

  switch (field.fieldType) {
    case 'textarea':
      return (
        <TextField
          label={label}
          multiline
          minRows={3}
          fullWidth
          error={error}
          helperText={field.helperText ?? undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'select':
      return (
        <TextField
          label={label}
          select
          fullWidth
          error={error}
          helperText={field.helperText ?? undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {opts.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      )
    case 'radio':
      return (
        <FormControl error={error}>
          <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>{label}</FormLabel>
          <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            {opts.map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                control={<Radio />}
                label={opt}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )
    default:
      return (
        <TextField
          label={label}
          fullWidth
          error={error}
          helperText={field.helperText ?? undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
  }
}
